'use client';

import { useState, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Settings, Scale, Pilcrow, ScrollText, BookText, Library, Gavel, Check, Palette, Sparkles, RotateCcw, Info, Cpu, Loader2, PlugZap, Power, AudioLines } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { SiFlag } from '@/components/chat/ModelBadge';
import { useModelStore } from '@/lib/stores/model-store';
import { CHAT_MODELS, type ChatModelId } from '@/lib/ai/chat-models';
import { RERANKERS, type RerankerId } from '@/lib/ai/rerankers';
import { LAB_EMBED_OPTIONS, LAB_WEIGHT_OPTIONS, LAB_FLASH_OPTIONS, SEARCH_LAB_DEFAULT, isSearchLabDefault } from '@/lib/ai/search-lab';
import { useFeatures } from '@/lib/hooks/use-features';
import { APP_VERSION, isProdBuild } from '@/lib/version';
import {
  useSettings,
  ASSISTANT_ICONS,
  DARK_THEMES,
  LIGHT_THEMES,
  SKINS,
  BG_PATTERNS,
  AssistantIconId,
  DarkThemeId,
  LightThemeId,
  SkinId,
  BgPatternId,
} from '@/lib/hooks/use-settings';
import { PisrsIcon } from '@/components/icons/PisrsIcon';
import { ServicesStatus } from './ServicesStatus';
import { VoiceSettings } from './VoiceSettings';
import {
  MoneyIcon,
  ClockIcon,
  ChartIcon,
  StarIcon,
  BellIcon,
  CheckCircleIcon,
  ShieldIcon,
  FlameIcon,
  TribonijanIcon,
} from '@/components/icons/ColorfulIcons';

// Map icon IDs to components (Lucide + custom + colorful)
const IconComponents: Record<AssistantIconId, React.ComponentType<{ className?: string }>> = {
  // Standard icons
  pisrs: PisrsIcon,
  scale: Scale,
  pilcrow: Pilcrow,
  scrollText: ScrollText,
  bookText: BookText,
  library: Library,
  gavel: Gavel,
  // Colorful icons
  money: MoneyIcon,
  clock: ClockIcon,
  chart: ChartIcon,
  star: StarIcon,
  bell: BellIcon,
  checkCircle: CheckCircleIcon,
  shield: ShieldIcon,
  flame: FlameIcon,
  tribonijan: TribonijanIcon,
};

// Theme button component for cleaner code (works with both light and dark themes)
function ThemeButton({
  theme,
  isSelected,
  onClick,
  compact = false
}: {
  theme: { label: string; description: string; background: string; card: string };
  isSelected: boolean;
  onClick: () => void;
  compact?: boolean;
}) {
  if (compact) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all',
          'hover:bg-muted/50',
          isSelected
            ? 'border-primary bg-primary/5'
            : 'border-transparent bg-muted/30'
        )}
      >
        {/* Color preview */}
        <div className="relative h-8 w-8 rounded-md border border-border flex-shrink-0 overflow-hidden">
          <div
            className="absolute inset-0"
            style={{ backgroundColor: theme.background }}
          />
          <div
            className="absolute bottom-0 right-0 w-3 h-3 rounded-tl-sm"
            style={{ backgroundColor: theme.card }}
          />
          {isSelected && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/20">
              <Check className="h-4 w-4 text-white drop-shadow-md" />
            </div>
          )}
        </div>
        <div className="text-[10px] font-medium truncate max-w-full">{theme.label}</div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-3 p-2.5 rounded-lg border-2 transition-all text-left',
        'hover:bg-muted/50',
        isSelected
          ? 'border-primary bg-primary/5'
          : 'border-transparent bg-muted/30'
      )}
    >
      {/* Color preview with layered effect */}
      <div className="relative h-8 w-8 rounded-md border border-border flex-shrink-0 overflow-hidden">
        <div
          className="absolute inset-0"
          style={{ backgroundColor: theme.background }}
        />
        <div
          className="absolute bottom-0 right-0 w-4 h-4 rounded-tl-md"
          style={{ backgroundColor: theme.card }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium truncate">{theme.label}</div>
        <div className="text-xs text-muted-foreground truncate">{theme.description}</div>
      </div>
      {isSelected && (
        <Check className="h-4 w-4 text-primary flex-shrink-0" />
      )}
    </button>
  );
}

interface SettingsDialogProps {
  trigger?: React.ReactNode;
}

export function SettingsDialog({ trigger }: SettingsDialogProps) {
  const { settings, setAssistantIcon, setDarkTheme, setLightTheme, setSkin, setBgPattern } = useSettings();
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [voiceBusy, setVoiceBusy] = useState(false);
  const [devMsg, setDevMsg] = useState<string | null>(null);

  // Allow opening from UserMenu via custom event
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener('open-settings', handler);
    return () => window.removeEventListener('open-settings', handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="ghost" size="icon" className="h-8 w-8">
            <Settings className="h-4 w-4" />
            <span className="sr-only">Nastavitve</span>
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Nastavitve</DialogTitle>
          <DialogDescription>
            Prilagodi videz aplikacije
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-120px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Slog vmesnika (skin) — smer vizualnega jezika */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4" />
                Slog vmesnika
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(SKINS) as SkinId[]).map((skinId) => {
                  const skin = SKINS[skinId];
                  const isSelected = settings.skin === skinId;
                  return (
                    <button
                      key={skinId}
                      type="button"
                      onClick={() => setSkin(skinId)}
                      className={cn(
                        'relative flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left',
                        'hover:bg-muted/50',
                        isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
                      )}
                    >
                      <div
                        className="h-8 w-8 rounded-full flex-shrink-0 border border-border"
                        style={{ background: `radial-gradient(circle at 35% 30%, ${skin.brand}, color-mix(in oklab, ${skin.brand} 50%, #000))` }}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={{ fontFamily: 'var(--font-cormorant), serif', letterSpacing: '0.03em' }}>{skin.label}</div>
                        <div className="text-[11px] text-muted-foreground truncate">{skin.description}</div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
              {/* Dekorativno ozadje */}
              <div className="grid grid-cols-4 gap-1.5 pt-1">
                {(Object.keys(BG_PATTERNS) as BgPatternId[]).map((bgId) => {
                  const bg = BG_PATTERNS[bgId];
                  const isSelected = settings.bgPattern === bgId;
                  return (
                    <button
                      key={bgId}
                      type="button"
                      onClick={() => setBgPattern(bgId)}
                      title={bg.description}
                      className={cn(
                        'flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all',
                        'hover:bg-muted/50',
                        isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
                      )}
                    >
                      <div className="text-[11px] font-medium">{bg.label}</div>
                    </button>
                  );
                })}
              </div>
              <p className="text-xs text-muted-foreground">
                Naglasna barva in ozadje prve strani. Barvno ozadje izbereš spodaj.
              </p>
            </div>

            {/* Assistant Icon Selection with Tabs */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">Ikona pomočnika</Label>
              <Tabs defaultValue="standard" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-3">
                  <TabsTrigger value="standard" className="gap-1.5">
                    <Scale className="h-3.5 w-3.5" />
                    Klasične
                  </TabsTrigger>
                  <TabsTrigger value="colorful" className="gap-1.5">
                    <Sparkles className="h-3.5 w-3.5" />
                    Barvne
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="standard" className="mt-0">
                  <div className="grid grid-cols-4 gap-2">
                    {(Object.keys(ASSISTANT_ICONS) as AssistantIconId[])
                      .filter(id => !ASSISTANT_ICONS[id].colorful)
                      .map((iconId) => {
                        const icon = ASSISTANT_ICONS[iconId];
                        const IconComponent = IconComponents[iconId];
                        const isSelected = settings.assistantIcon === iconId;

                        return (
                          <button
                            key={iconId}
                            onClick={() => setAssistantIcon(iconId)}
                            className={cn(
                              'relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all',
                              'hover:bg-muted/50',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-transparent bg-muted/30'
                            )}
                          >
                            {isSelected && (
                              <div className="absolute top-1 right-1">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                            )}
                            <div className={cn(
                              'h-9 w-9 rounded-full flex items-center justify-center',
                              'bg-primary text-primary-foreground'
                            )}>
                              <IconComponent className="h-4 w-4" />
                            </div>
                            <div className="text-center">
                              <div className="text-[11px] font-medium leading-tight">{icon.label}</div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </TabsContent>

                <TabsContent value="colorful" className="mt-0">
                  <div className="grid grid-cols-4 gap-2">
                    {(Object.keys(ASSISTANT_ICONS) as AssistantIconId[])
                      .filter(id => ASSISTANT_ICONS[id].colorful)
                      .map((iconId) => {
                        const icon = ASSISTANT_ICONS[iconId];
                        const IconComponent = IconComponents[iconId];
                        const isSelected = settings.assistantIcon === iconId;

                        return (
                          <button
                            key={iconId}
                            onClick={() => setAssistantIcon(iconId)}
                            className={cn(
                              'relative flex flex-col items-center gap-1.5 p-2 rounded-lg border-2 transition-all',
                              'hover:bg-muted/50',
                              isSelected
                                ? 'border-primary bg-primary/5'
                                : 'border-transparent bg-muted/30'
                            )}
                          >
                            {isSelected && (
                              <div className="absolute top-1 right-1">
                                <Check className="h-3 w-3 text-primary" />
                              </div>
                            )}
                            {/* Colorful icons on dark background to pop */}
                            <div className="h-9 w-9 rounded-full flex items-center justify-center bg-gray-900 dark:bg-gray-950">
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div className="text-center">
                              <div className="text-[11px] font-medium leading-tight">{icon.label}</div>
                              <div className="text-[10px] text-muted-foreground">{icon.description}</div>
                            </div>
                          </button>
                        );
                      })}
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Light Theme Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Palette className="h-4 w-4" />
                🌞 Svetlo ozadje
              </Label>
              <Tabs defaultValue="light-neutrals" className="w-full">
                <TabsList className="grid w-full grid-cols-4 mb-3 h-auto">
                  <TabsTrigger value="light-neutrals" className="text-[10px] px-1">Nevtralne</TabsTrigger>
                  <TabsTrigger value="light-blues" className="text-[10px] px-1">Modre</TabsTrigger>
                  <TabsTrigger value="light-greens" className="text-[10px] px-1">Zelene</TabsTrigger>
                  <TabsTrigger value="light-purples" className="text-[10px] px-1">Vijolične</TabsTrigger>
                </TabsList>
                <TabsList className="grid w-full grid-cols-4 mb-3 h-auto">
                  <TabsTrigger value="light-pinks" className="text-[10px] px-1">Roza</TabsTrigger>
                  <TabsTrigger value="light-warm" className="text-[10px] px-1">Tople</TabsTrigger>
                  <TabsTrigger value="light-yellow" className="text-[10px] px-1">Rumene</TabsTrigger>
                  <TabsTrigger value="light-special" className="text-[10px] px-1">Posebne</TabsTrigger>
                </TabsList>

                <TabsContent value="light-neutrals" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['white', 'snow', 'ivory', 'silver'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-blues" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['skyBlue', 'babyBlue', 'ice', 'azure'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-greens" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['mint', 'sage', 'honeydew', 'pistachio', 'aqua', 'seafoam'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-purples" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['lavender', 'lilac', 'periwinkle', 'wisteria'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-pinks" className="mt-0">
                  <div className="grid grid-cols-5 gap-1.5">
                    {(['blush', 'rose', 'peony', 'sakura', 'bubblegum'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-warm" className="mt-0">
                  <div className="grid grid-cols-3 gap-1.5">
                    {(['peach', 'apricot', 'coral'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-yellow" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['cream', 'vanilla', 'lemon', 'champagne'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>

                <TabsContent value="light-special" className="mt-0">
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['sand', 'linen', 'parchment', 'cotton'] as LightThemeId[]).map((themeId) => (
                      <ThemeButton
                        key={themeId}
                        theme={LIGHT_THEMES[themeId]}
                        isSelected={settings.lightTheme === themeId}
                        onClick={() => setLightTheme(themeId)}
                        compact
                      />
                    ))}
                  </div>
                </TabsContent>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                * Vidno v svetlem načinu
              </p>
            </div>

            {/* Dark Theme Selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Palette className="h-4 w-4" />
                🌙 Temno ozadje
              </Label>
              <Tabs defaultValue="dark-neutrals" className="w-full">
                <TabsList className="grid w-full grid-cols-3 mb-3">
                  <TabsTrigger value="dark-neutrals" className="text-xs">Nevtralne</TabsTrigger>
                  <TabsTrigger value="dark-cool" className="text-xs">Hladne</TabsTrigger>
                  <TabsTrigger value="dark-warm" className="text-xs">Tople</TabsTrigger>
                </TabsList>

                <TabsContent value="dark-neutrals" className="mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    {(['black', 'charcoal', 'graphite', 'blue'] as DarkThemeId[]).map((themeId) => {
                      const theme = DARK_THEMES[themeId];
                      const isSelected = settings.darkTheme === themeId;

                      return (
                        <ThemeButton
                          key={themeId}
                          theme={theme}
                          isSelected={isSelected}
                          onClick={() => setDarkTheme(themeId)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="dark-cool" className="mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    {(['navy', 'teal', 'forest', 'emerald', 'purple', 'grape'] as DarkThemeId[]).map((themeId) => {
                      const theme = DARK_THEMES[themeId];
                      const isSelected = settings.darkTheme === themeId;

                      return (
                        <ThemeButton
                          key={themeId}
                          theme={theme}
                          isSelected={isSelected}
                          onClick={() => setDarkTheme(themeId)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>

                <TabsContent value="dark-warm" className="mt-0">
                  <div className="grid grid-cols-2 gap-2">
                    {(['rose', 'wine', 'amber'] as DarkThemeId[]).map((themeId) => {
                      const theme = DARK_THEMES[themeId];
                      const isSelected = settings.darkTheme === themeId;

                      return (
                        <ThemeButton
                          key={themeId}
                          theme={theme}
                          isSelected={isSelected}
                          onClick={() => setDarkTheme(themeId)}
                        />
                      );
                    })}
                  </div>
                </TabsContent>
              </Tabs>
              <p className="text-xs text-muted-foreground">
                * Vidno v temnem načinu
              </p>
            </div>

            {/* Jezikovni model (LLM) — preklop Gemini / GaMS / custom (GaMS migracija) */}
            <div className="pt-4 border-t border-border/50">
              <LlmModelSettings isAdmin={session?.user?.isAdmin === true} isResearcher={session?.user?.isResearcher === true} />
            </div>

            {/* Glas pomočnika — izbira TTS glasu + preizkus */}
            <div className="pt-4 border-t border-border/50">
              <VoiceSettings />
            </div>

            {/* Stanje storitev — API health dashboard */}
            <div className="pt-4 border-t border-border/50">
              <ServicesStatus />
            </div>

            {/* O aplikaciji — verzija + build podpis (sledljivost prod/dev) */}
            <div className="space-y-2 pt-4 border-t border-border/50">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Info className="h-4 w-4" />
                O aplikaciji
              </Label>
              <div className="rounded-lg bg-muted/30 p-3 text-xs space-y-1.5 font-mono">
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Okolje</span>
                  <span className={cn('truncate', isProdBuild ? '' : 'text-amber-600 dark:text-amber-400')}>
                    {isProdBuild ? 'PROD · app.sersen.eu' : 'DEV · dev.sersen.eu'}
                  </span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Verzija</span>
                  <span>v{APP_VERSION.version}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Veja</span>
                  <span className="truncate max-w-[60%]">{APP_VERSION.branch}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Commit</span>
                  <span>{APP_VERSION.sha}</span>
                </div>
                <div className="flex justify-between gap-2">
                  <span className="text-muted-foreground">Build</span>
                  <span>{new Date(APP_VERSION.builtAt).toLocaleString('sl-SI')}</span>
                </div>
              </div>
            </div>

            {/* Dev Tools — samo admin (ADMIN_EMAILS) in samo v dev (prod teče prek systemd) */}
            {session?.user?.isAdmin && !isProdBuild && (
              <div className="space-y-3 pt-4 border-t border-dashed border-amber-500/30">
                <Label className="text-sm font-medium flex items-center gap-2 text-amber-600 dark:text-amber-400">
                  <RotateCcw className="h-4 w-4" />
                  Dev orodja
                </Label>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                    disabled={restarting || stopping}
                    onClick={async () => {
                      setRestarting(true);
                      setDevMsg(null);
                      try {
                        const res = await fetch('/api/dev/restart', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ action: 'restart' }),
                        });
                        if (res.ok) {
                          setDevMsg('Restart sprožen — čakam, da se strežnik vrne ...');
                          // Server pade čez ~0.5s, nato poll do vrnitve
                          setTimeout(() => {
                            const poll = setInterval(async () => {
                              try {
                                const health = await fetch('/api/health', { signal: AbortSignal.timeout(2000) });
                                if (health.ok) {
                                  clearInterval(poll);
                                  window.location.reload();
                                }
                              } catch {
                                // Server still restarting
                              }
                            }, 1500);
                            // Stop polling after 45s (predev korak traja nekaj sekund)
                            setTimeout(() => clearInterval(poll), 45000);
                          }, 2000);
                        } else {
                          setRestarting(false);
                          setDevMsg('Restart ni uspel: ' + (await res.text()));
                        }
                      } catch {
                        setRestarting(false);
                        setDevMsg('Restart ni uspel (povezava).');
                      }
                    }}
                  >
                    {restarting ? (
                      <>
                        <RotateCcw className="h-3.5 w-3.5 mr-2 animate-spin" />
                        Restartiram ...
                      </>
                    ) : (
                      <>
                        <RotateCcw className="h-3.5 w-3.5 mr-2" />
                        Restart serverja
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-red-500/30 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                    disabled={restarting || stopping}
                    onClick={async () => {
                      if (!window.confirm('Ugasnem dev strežnik? Nazaj ga zaženeš s "systemctl start tribonianus-dev" ali "npm run dev".')) return;
                      setStopping(true);
                      setDevMsg(null);
                      try {
                        const res = await fetch('/api/dev/restart', {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ action: 'stop' }),
                        });
                        const data = await res.json().catch(() => null);
                        setDevMsg(res.ok ? (data?.message ?? 'Strežnik se ugaša.') : 'Izklop ni uspel: ' + (data?.error ?? res.status));
                      } catch {
                        setDevMsg('Izklop ni uspel (povezava).');
                      } finally {
                        setStopping(false);
                      }
                    }}
                  >
                    <Power className="h-3.5 w-3.5 mr-2" />
                    Ugasni server
                  </Button>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full border-amber-500/30 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                  disabled={voiceBusy}
                  onClick={async () => {
                    setVoiceBusy(true);
                    setDevMsg(null);
                    try {
                      const res = await fetch('/api/dev/voice-restart', { method: 'POST' });
                      const data = await res.json().catch(() => null);
                      setDevMsg(res.ok
                        ? `Glas (Whisper + Piper) restartiran. ${data?.status ?? ''}`
                        : (data?.error ?? `Restart glasu ni uspel (${res.status})`));
                    } catch {
                      setDevMsg('Restart glasu ni uspel (povezava).');
                    } finally {
                      setVoiceBusy(false);
                    }
                  }}
                >
                  {voiceBusy ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                      Restartiram glasovna kontejnerja ...
                    </>
                  ) : (
                    <>
                      <AudioLines className="h-3.5 w-3.5 mr-2" />
                      Restart glasu (Whisper + Piper)
                    </>
                  )}
                </Button>
                {devMsg && (
                  <p className="text-[11px] text-muted-foreground whitespace-pre-wrap">{devMsg}</p>
                )}
                <p className="text-[10px] text-muted-foreground">
                  Server teče prek systemd (tribonianus-dev) — restart počisti vse procese in preživi
                  restart LXC. Glasovni gumb restarta samo kontejnerja na voice LXC (ne Frigate/NVR);
                  Azure ne potrebuje restarta.
                </p>
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Izbira jezikovnega modela (LLM) — Gemini / GaMS / custom endpoint.
 * GaMS migracija: preklop je per-uporabnik (localStorage), pošlje se na /api/chat.
 * Custom endpoint (CLORE.AI ipd.) je viden samo adminu; server neodvisno preveri pravico.
 */
function LlmModelSettings({ isAdmin, isResearcher }: { isAdmin: boolean; isResearcher: boolean }) {
  const { modelId, customBaseUrl, customModel, customApiKey, setModelId, setCustom, rerankerId, setRerankerId } = useModelStore();
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Ne-admin ne sme izbrati custom → skrij ga iz seznama.
  const modelIds = (Object.keys(CHAT_MODELS) as ChatModelId[]).filter(
    (id) => id !== 'custom' || isAdmin
  );

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch('/api/llm/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          modelId === 'custom' && customBaseUrl
            ? { baseUrl: customBaseUrl, apiKey: customApiKey || undefined }
            : {}
        ),
      });
      const data = await res.json();
      if (data.ok) {
        setTestResult({ ok: true, msg: `OK (${data.latencyMs} ms) · ${(data.models || []).join(', ') || 'brez seznama'}` });
      } else {
        setTestResult({ ok: false, msg: data.error || 'Napaka' });
      }
    } catch (e) {
      setTestResult({ ok: false, msg: e instanceof Error ? e.message : 'Napaka povezave' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium flex items-center gap-2">
        <Cpu className="h-4 w-4" />
        Jezikovni model (LLM)
      </Label>
      <div className="grid grid-cols-1 gap-2">
        {modelIds.map((id) => {
          const meta = CHAT_MODELS[id];
          const isSelected = modelId === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setModelId(id)}
              className={cn(
                'relative flex items-start gap-3 p-2.5 rounded-lg border-2 transition-all text-left',
                'hover:bg-muted/50',
                isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
              )}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  {meta.slovenian && <SiFlag className="h-3 w-[18px]" />}
                  <span className="text-sm font-medium truncate">{meta.label}</span>
                  <span className="text-[10px] text-muted-foreground">
                    · {Math.round(meta.contextTokens / 1000)}k
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">{meta.description}</div>
              </div>
              {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />}
            </button>
          );
        })}
      </div>

      {/* Admin: custom OpenAI-compatible endpoint (CLORE.AI ipd.) */}
      {isAdmin && modelId === 'custom' && (
        <div className="space-y-2 rounded-lg border border-dashed border-primary/30 p-3">
          <p className="text-[11px] text-muted-foreground">
            Lasten OpenAI-compatible endpoint. Spinaš pod (npr. CLORE.AI), prilepiš URL — brez redeploya.
          </p>
          <div className="space-y-1.5">
            <Label className="text-xs">Base URL (…/v1)</Label>
            <Input
              value={customBaseUrl}
              onChange={(e) => setCustom({ customBaseUrl: e.target.value })}
              placeholder="https://xxxxx.clore.ai/v1"
              className="h-8 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Ime modela</Label>
            <Input
              value={customModel}
              onChange={(e) => setCustom({ customModel: e.target.value })}
              placeholder="npr. GaMS2-27B-Instruct"
              className="h-8 text-xs font-mono"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">API ključ (opcijsko)</Label>
            <Input
              type="password"
              value={customApiKey}
              onChange={(e) => setCustom({ customApiKey: e.target.value })}
              placeholder="sk-…"
              className="h-8 text-xs font-mono"
            />
          </div>
        </div>
      )}

      {/* Preizkus povezave — samo admin (uporabi /api/llm/test) */}
      {isAdmin && (modelId === 'custom' || CHAT_MODELS[modelId].provider === 'gams') && (
        <div className="space-y-1.5">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            disabled={testing}
            onClick={testConnection}
          >
            {testing ? (
              <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Preizkušam…</>
            ) : (
              <><PlugZap className="h-3.5 w-3.5 mr-2" /> Preizkusi povezavo</>
            )}
          </Button>
          {testResult && (
            <p className={cn('text-[11px] break-words', testResult.ok ? 'text-green-600 dark:text-green-400' : 'text-destructive')}>
              {testResult.msg}
            </p>
          )}
        </div>
      )}

      <p className="text-xs text-muted-foreground">
        Preklop velja za odgovore v chatu. Nomotehnika in interne naloge ostanejo na Gemini.
      </p>

      {/* GraphRAG korak 3c: opt-in rerank (PoC eksperiment) — CE modeli ali
          Flash listwise (TODO #5, seja 279: edini nad baselineom).
          Deluje samo v paket-poti za mala okna (GaMS + SMALL_MODEL_CONTEXT);
          na Gemini poti je no-op — demo ostane hiter. */}
      <div className="space-y-2 pt-2 border-t">
        <Label className="text-sm font-medium">Rerank kandidatov (eksperimentalno)</Label>
        <div className="grid grid-cols-1 gap-2">
          {([
            { id: 'none' as const, label: 'Brez reranka (privzeto)', description: 'Obstoječi deterministični vrstni red — priporočeno za demo.' },
            ...Object.entries(RERANKERS).map(([id, m]) => ({ id: id as Exclude<RerankerId, 'none'>, label: m.label, description: m.description })),
          ]).map((opt) => {
            const isSelected = rerankerId === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setRerankerId(opt.id)}
                className={cn(
                  'relative flex items-start gap-3 p-2.5 rounded-lg border-2 transition-all text-left hover:bg-muted/50',
                  isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
                )}
              >
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium">{opt.label}</span>
                  <div className="text-xs text-muted-foreground">{opt.description}</div>
                </div>
                {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />}
              </button>
            );
          })}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Preureja člene v paketu za modele z malimi okni (GaMS). Pri Geminiju nima učinka.
          CE modela prvi vklop naložita (~30 s), CPU inference doda nekaj sekund; Gemini
          Flash (listwise) ne nalaga ničesar — en API klic (~1–2 s).
        </p>
      </div>

      {/* Iskalni laboratorij: skrbnik ali raziskovalec (seja 401); ali je na instanci sploh
          vklopljen, pove features.searchLab (PISRS_SEARCH_LAB=1), ne vrsta gradnje. */}
      {(isAdmin || isResearcher) && <SearchLabSettings />}
    </div>
  );
}

/**
 * Iskalni laboratorij (seja 384): preizkus iskanja po členih v živo. Viden skrbniku na dev
 * instanci s PISRS_SEARCH_LAB=1; strežnik pravico preveri neodvisno. Privzete izbire iskanja
 * ne spremenijo. Meritve: docs/teorija/VLOZITVENI_MODEL_V_HIBRIDU.md.
 */
function SearchLabSettings() {
  const features = useFeatures();
  const searchLab = useModelStore((s) => s.searchLab) ?? SEARCH_LAB_DEFAULT;
  const setSearchLab = useModelStore((s) => s.setSearchLab);
  if (!features.searchLab) return null;

  const group = <T extends string>(
    title: string,
    options: { id: T; label: string; description: string }[],
    value: T,
    onPick: (id: T) => void,
  ) => (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-muted-foreground">{title}</div>
      <div className="grid grid-cols-1 gap-1.5">
        {options.map((opt) => {
          const isSelected = value === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              onClick={() => onPick(opt.id)}
              data-testid={`search-lab-${title}-${opt.id}`}
              className={cn(
                'relative flex items-start gap-3 p-2 rounded-lg border-2 transition-all text-left hover:bg-muted/50',
                isSelected ? 'border-primary bg-primary/5' : 'border-transparent bg-muted/30'
              )}
            >
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium">{opt.label}</span>
                <div className="text-xs text-muted-foreground">{opt.description}</div>
              </div>
              {isSelected && <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="space-y-3 pt-2 border-t" data-testid="search-lab">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-sm font-medium">Iskanje po členih (laboratorij)</Label>
        {!isSearchLabDefault(searchLab) && (
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setSearchLab(SEARCH_LAB_DEFAULT)}>
            <RotateCcw className="h-3 w-3 mr-1" /> Na privzeto
          </Button>
        )}
      </div>
      {group('Vložitveni model', LAB_EMBED_OPTIONS, searchLab.embed, (embed) => setSearchLab({ embed }))}
      {group('Utež vektorja v hibridu', LAB_WEIGHT_OPTIONS, searchLab.weight, (weight) => setSearchLab({ weight }))}
      {group('Flash rerank pri členih', LAB_FLASH_OPTIONS, searchLab.flash, (flash) => setSearchLab({ flash }))}
      {features.searchDefaults && (
        <p className="text-[11px] text-muted-foreground" data-testid="search-lab-defaults">
          Privzeto na tej instanci: {features.searchDefaults.embed}, utež {features.searchDefaults.weight.replace('.', ',')}, {features.searchDefaults.flash}.
        </p>
      )}
      <p className="text-[11px] text-muted-foreground">
        Velja za naslednja vprašanja v chatu. Ob odgovoru se izpiše, katera nastavitev je bila
        dejansko uporabljena. Arctic pokriva ves korpus po stanju 29. 9. 2026; koščkov, ki jih
        je nočna sinhronizacija dodala pozneje, ne pozna. Številke v opisih: sidro je merilnik
        sidra (186 vprašanj, iskanje znotraj zakona), golden v4 je iskanje čez ves korpus
        (preskusni del, 258 vprašanj), oboje brez Flasha.
      </p>
    </div>
  );
}

/**
 * Export the icon component getter for use in other components
 */
export function getAssistantIcon(iconId: AssistantIconId): React.ComponentType<{ className?: string }> {
  return IconComponents[iconId];
}
